/**
 * Keeps reusable blocklists discoverable while reserving creation for users
 * who can manage them.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';
import { CreationSource, ConfigurationType } from 'src/constants';
import Table, {
  TableRow,
  TableCell,
  TableActionBar,
} from '@amzn/meridian/table';
import { type TableSortDirection } from '@amzn/meridian/table/table';
import { useTranslation } from 'src/arb-wrapper';
import { Loader } from 'src/components/common/Loader';
import { NoResultsTableRow } from 'src/components/common/data-table-action-bar/NoResultsTableRow';
import { TablePagination } from 'src/components/common/data-table-action-bar/TablePagination';
import { type Filter, TokenizedSearchField } from 'src/components/common/tokenized-search-field';
import { type CampaignConfigurationDto } from 'src/store/generated/leela-api-generated';
import { useCampaignConfigurationControllerFindManyQuery } from 'src/store/leela-api';
import { BlocklistTableRow } from 'src/components/blocklists/BlocklistTableRow';
import { useCanPerform } from 'src/hooks/useCanPerform';
import { buildRegexFilters } from 'src/utils/filterUtils';

export const BlocklistList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canManageBlocklists = useCanPerform('MANAGE_BLOCKLISTS');
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    pageCount: 1,
  });

  const [sort, setSort] = useState<string[]>(['updatedAt:-1']);
  const [sortColumn, setSortColumn] = useState('updatedAt');
  const [sortDirection, setSortDirection] = useState<TableSortDirection>('descending');
  const [filters, setFilters] = useState<Record<string, object>>({});

  const convertSortDirection = (direction: string) => direction === 'ascending' ? 1 : -1;

  useEffect(() => {
    setSort([`${sortColumn}:${convertSortDirection(sortDirection)}`]);
  }, [sortColumn, sortDirection]);

  const onSort = ({ sortColumn, sortDirection }: { sortColumn: string; sortDirection: TableSortDirection }) => {
    setPagination(prevState => ({
      ...prevState,
      page: 1,
    }));
    setSortColumn(sortColumn);
    setSortDirection(sortDirection);
  };

  const onFiltersChanged = (filters: Filter[]) => {
    setPagination(prevState => ({
      ...prevState,
      page: 1,
    }));

    setFilters(buildRegexFilters(filters, 'name'));
  };

  // On page 1: fetch global blocklist separately (only when no filters)
  const { data: globalBlocklistData } = useCampaignConfigurationControllerFindManyQuery({
    configType: ConfigurationType.GTI_BLOCKLIST,
    source: CreationSource.ADMIN,
    pageSize: 1,
    includeCampaignCount: false,
  }, {
    skip: pagination.page !== 1 || Object.keys(filters).length > 0,
  });

  // Fetch regular blocklists (USER source)
  const { data, isLoading } = useCampaignConfigurationControllerFindManyQuery({
    configType: ConfigurationType.GTI_BLOCKLIST,
    source: CreationSource.USER,
    pageSize: pagination.pageSize,
    page: pagination.page,
    sort,
    filters,
    includeCampaignCount: true,
  });

  const paginationResponse = data?.meta.pagination;
  const configs = pagination.page === 1 && Object.keys(filters).length === 0
    ? [...(globalBlocklistData?.data ?? []), ...(data?.data ?? [])]
    : (data?.data ?? []);

  useEffect(() => {
    setPagination(prevState => ({
      ...prevState,
      page: paginationResponse?.page ?? 1,
      pageCount: paginationResponse?.pageCount ?? 1,
    }));
  }, [paginationResponse]);

  return (
    <Column>
      <Heading level={1}>{t('blocklists')}</Heading>

      <Loader isLoading={isLoading}>
        <Table
          headerRows={1}
          rowComponents={[TableRow, BlocklistTableRow, NoResultsTableRow]}
          showDividers
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={onSort}
        >
          <TableActionBar alignmentHorizontal="justify" widths={['fill', 'fit']}>
            <Row spacing="200">
              <TokenizedSearchField
                scopes={['name', 'owner']}
                onFiltersChange={onFiltersChanged}
              />
            </Row>
            {canManageBlocklists
              ? (
              <Button
                type="primary"
                onClick={() => {
                  navigate('/blocklists/create');
                }}
              >
                {t('blocklist_create')}
              </Button>
                )
              : undefined}
          </TableActionBar>

          {/* Header Row */}
          <TableRow>
            <TableCell sortColumn="name">{t('form_global_name')}</TableCell>
            <TableCell sortColumn="owner">{t('program_owner')}</TableCell>
            <TableCell sortColumn="config.gtis">{t('blocklist_gti_count')}</TableCell>
            <TableCell sortColumn="campaignCount">{t('blocklist_campaign_count')}</TableCell>
            <TableCell sortColumn="updatedAt">
              {t('program_header_last_updated')}
            </TableCell>
          </TableRow>

          {/* Entries */}
          {configs.length === 0 && <NoResultsTableRow span={6} />}
          {configs.map((config: CampaignConfigurationDto) => (
            <BlocklistTableRow key={config.id} {...config} />
          ))}
        </Table>

        <TablePagination
          currentPage={pagination.page}
          itemsPerPage={pagination.pageSize}
          pageCount={pagination.pageCount}
          onItemsPerPageChange={pageSize => {
            setPagination(prevState => ({
              ...prevState,
              page: 1,
              pageSize,
            }));
          }}
          onPageChange={newPage => {
            setPagination(prevState => ({
              ...prevState,
              page: newPage,
            }));
          }}
        />
      </Loader>
    </Column>
  );
};
